/* eslint-disable react/destructuring-assignment */
import { DataSource } from 'hooks/useHttpClient';
import moment from 'moment';
import { BasicMappedStatus } from './Candidate';
import { Partner, PartnerSingleResolver } from './Company';
import { SignupFormState } from 'pages/Login/SignupForm';

export const ConfiguredMasterCompanyId = 12;

export type Address = {
    fullStreetSddress: string | null;
    zipCode: string | null;
    phoneNumber: string | null;
    timeZone: string | null;
    location: boolean | null;
    emergencyPhoneNumber: string | null;
    exteriorNumber: string | null;
    interiorNumber: string | null;
    countryCode: string | null;
    stateCode: string | null;
    city: string | null;
};

// This would be removed once we migrate everything to the new API
export type LegacyAddress = {
    country: BasicMappedStatus | null;
    state: BasicMappedStatus | null;
    city: BasicMappedStatus | null;
};

export type FactAudit = {
    userId: number;
    userFullName: string;
    avatar?: string;
    dateStr: string;
};

export type LoginResponse = {
    token: string;
    user?: OwnUser;
};

export type GoogleResponse = {
    token: string;
};

export type OwnUser = {
    hyrId: number;
    id: number | string;
    avatarUrl: string | null;
    firstName: string;
    lastName: string;
    companyRole: string;
    jobTitle: string;
    hasPartnerAccess: boolean;
};

export type OwnUserWithCompany = OwnUser & {
    company: {
        id: number;
        name: string;
        logo?: string;
        partner?: Partner;
    };
    pendingAgreement?:
        | {
              id: number;
              name: string;
              fileUrl: string;
          }
        | undefined;
};

export type PaginationMeta = {
    pageCount: number;
    totalCount: number;
    perPage: number;
};

export type SelectItemType = {
    label: string;
    value: string;
};

export type PaginatedList<T> = {
    data: T[];
    meta: PaginationMeta;
};

export type Notification = {
    id: number;
    modelId: number;
    modelType: string;
    message: string;
    content: string;
    companyId: number | null;
    createdBy: number | null;
    createdByAvatar: string | null;
    companyAvatar: string | null;
    createdAt: string;
    link: string;
};

export const systemDepartments: BasicMappedStatus[] = [
    { id: 1, name: 'Billing' },
    { id: 2, name: 'Delivery' },
    { id: 3, name: 'Finance' },
    { id: 4, name: 'Operations' },
    { id: 5, name: 'Cyber Security' },
    { id: 6, name: 'IT' },
    { id: 7, name: 'Product' },
    { id: 8, name: 'Technology' },
    { id: 9, name: 'R&D' },
    { id: 10, name: 'Recruiting' },
    { id: 11, name: 'HR' },
    { id: 12, name: 'Marketing' },
    { id: 13, name: 'Sales' }
];

export const getMomentAgoText = (date?: string | null) => {
    if (!date) return 'Not set';

    return moment(date).fromNow();
};

export const getFormattedDateText = (date?: string | null, format: string = 'YYYY, MMMM DD') => {
    if (!date) return 'Not set';

    return moment(date).format(format);
};

export const getAuditText = (audit?: FactAudit, format: string = 'YYYY, MMMM DD') => {
    if (!audit) return 'Not set';

    const date = moment(audit.dateStr).format(format);
    let authorName = '';
    if (audit.userFullName) {
        authorName = `by ${audit.userFullName}`;
    }
    return `${date} ${authorName}`;
};

export const getAddressText = (address?: Address | null) => {
    if (!address) return 'Not set';

    const addressText = [address.fullStreetSddress, address.city, address.stateCode, address.countryCode].filter(Boolean).join(', ').trim();

    return addressText.length > 0 ? addressText : 'Not set';
};

export const getTimezoneText = (address?: Address | null) => {
    if (!address) return 'Not set';

    return address.timeZone || 'Not set';
};

export function LoginResolver(dataSource: DataSource, apiResponse: any): LoginResponse | null {
    switch (dataSource) {
        case 'hyrerocket':
            return {
                token: apiResponse.token
            } as LoginResponse;

        case 'micro-v3':
            return {
                token: apiResponse.accessCode
            } as LoginResponse;

        default:
            return null;
    }
}

export function ValidateInvitationResolver(dataSource: DataSource, apiResponse: any): SignupFormState | null {
    switch (dataSource) {
        case 'hyrerocket':
            if (apiResponse.is_valid) {
                return {
                    firstName: apiResponse.user?.first_name,
                    lastName: apiResponse.user?.last_name,
                    phone: apiResponse.user?.location?.phone_number,
                    jobTitle: apiResponse.user?.job_title,
                    department: apiResponse.user?.department,
                    bio: apiResponse.user?.bio
                };
            }
            return null;

        default:
            return null;
    }
}

export function PaginationMetaResolver(dataSource: DataSource, apiResponse: any): PaginationMeta | null {
    if (!apiResponse) {
        return null;
    }
    switch (dataSource) {
        case 'hyrerocket':
            return {
                pageCount: apiResponse.last_page,
                totalCount: apiResponse.total,
                perPage: apiResponse.per_page
            } as PaginationMeta;

        default:
            return null;
    }
}

export function AddressResolver(dataSource: DataSource, apiResponse: any): Address | null {
    if (!apiResponse) {
        return null;
    }
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    fullStreetSddress: apiResponse.full_street_address,
                    zipCode: apiResponse.zip_code,
                    phoneNumber: apiResponse.phone_number,
                    timeZone: apiResponse.time_zone,
                    location: apiResponse.location,
                    emergencyPhoneNumber: apiResponse.emergency_phone_number,
                    exteriorNumber: apiResponse.exterior_number,
                    interiorNumber: apiResponse.interior_number,
                    countryCode: apiResponse.country?.iso2 || null,
                    stateCode: apiResponse.state?.iso2 || null,
                    city: apiResponse.city?.name || null
                } as Address;

            default:
                return null;
        }
    } catch (error) {
        console.log('AddressResolver error', error);
        return null;
    }
}

export function LegacyAddressResolver(dataSource: DataSource, apiResponse: any): LegacyAddress | null {
    if (!apiResponse) {
        return null;
    }
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    country: apiResponse.country
                        ? {
                              id: apiResponse.country.id,
                              name: apiResponse.country.name
                          }
                        : null,
                    state: apiResponse.state
                        ? {
                              id: apiResponse.state.id,
                              name: apiResponse.state.name
                          }
                        : null,
                    city: apiResponse.city
                        ? {
                              id: apiResponse.city.id,
                              name: apiResponse.city.name
                          }
                        : null
                };

            default:
                return null;
        }
    } catch (error) {
        console.log('LegacyAddressResolver error', error);
        return null;
    }
}

export const ResolveMyself = (dataSource: DataSource, apiResponse: any): OwnUserWithCompany | null => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    hyrId: apiResponse.id,
                    id: apiResponse.id,
                    avatarUrl: apiResponse.profile_picture,
                    firstName: apiResponse.first_name,
                    lastName: apiResponse.last_name,
                    companyRole: apiResponse.roles[0], // TODO ADD MORE ROLES AS CONSTANTS
                    jobTitle: apiResponse.job_title,
                    hasPartnerAccess: apiResponse.partner_id !== null,
                    company: {
                        id: apiResponse.company[0]?.id || -1,
                        name: apiResponse.company[0]?.name || '',
                        logo: apiResponse.company[0]?.logo,
                        partner: PartnerSingleResolver(dataSource, apiResponse.company[0]?.partner) || undefined
                    },
                    pendingAgreement: apiResponse.agreement
                        ? {
                              id: apiResponse.agreement.id,
                              name: apiResponse.agreement.name,
                              fileUrl: apiResponse.agreement.document_url
                          }
                        : undefined
                } as OwnUserWithCompany;

            default:
                return null;
        }
    } catch (error) {
        console.log('ResolveMyself error', error);
        return null;
    }
};

export const ResolveHardwareList = (dataSource: DataSource, apiResponse: any): BasicMappedStatus[] => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return apiResponse.map((item: any) => ({
                    id: item.id,
                    name: item.type
                }));

            default:
                return [];
        }
    } catch (error) {
        console.log('resolveHardware error', error);
        return [];
    }
};

export const ResolvePermissions = (dataSource: DataSource, apiResponse: any): Record<string, string | boolean | number> => {
    try {
        switch (dataSource) {
            case 'micro-v3':
                return apiResponse.authorizations as Record<string, string | boolean | number>;

            case 'hyrerocket':
                return apiResponse.data as Record<string, string | boolean | number>;

            default:
                return {};
        }
    } catch (error) {
        console.log('ResolvePermissions error', error);
        return {};
    }
};

export const ResolveSingleNotification = (dataSource: DataSource, apiResponse: any): Notification | undefined => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    modelId: apiResponse.model_id,
                    modelType: apiResponse.model_type,
                    message: apiResponse.message,
                    companyId: apiResponse.company_id,
                    createdBy: apiResponse.created_by,
                    createdByAvatar: apiResponse.created_by_avatar,
                    companyAvatar: apiResponse.company_logo,
                    createdAt: apiResponse.created_at,
                    link: apiResponse.link,
                    content: apiResponse.content
                } as Notification;

            default:
                return undefined;
        }
    } catch (error) {
        console.log('ResolveSingleNotification error', error);
        return undefined;
    }
};

export const ResolveNotifications = (dataSource: DataSource, apiResponse: any): Notification[] => {
    if (!apiResponse) {
        return [];
    }
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return apiResponse.map((item: any) => ResolveSingleNotification(dataSource, item)).filter(Boolean) as Notification[];

            default:
                return [];
        }
    } catch (error) {
        console.log('ResolveNotifications error', error);
        return [];
    }
};
